import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { PageHeader } from "@/components/ui/page-header";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { 
  FileText, 
  Calendar, 
  Trash2, 
  Target,
  TrendingUp,
  Building,
  Eye,
  Download,
  ArrowUpDown,
  Star,
  BarChart3,
  ArrowLeft
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ResumeMatchTestService, ResumeMatchTest } from "@/services/resumeMatchTestService";
import { useAuth } from "@/hooks/useAuth";
import { ResumeMatchBreakdown } from "@/components/ResumeMatchBreakdown";

interface ResumeMatchTestLibraryProps {
  onBack: () => void;
}

export function ResumeMatchTestLibrary({ onBack }: ResumeMatchTestLibraryProps) {
  const [resumeTests, setResumeTests] = useState<ResumeMatchTest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [selectedTest, setSelectedTest] = useState<ResumeMatchTest | null>(null);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [selectedTests, setSelectedTests] = useState<Set<string>>(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [sortBy, setSortBy] = useState<string>('date-desc');
  
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      loadResumeTests();
    }
  }, [user]);

  const loadResumeTests = async () => {
    setIsLoading(true);
    try {
      const tests = await ResumeMatchTestService.getResumeMatchTests();
      setResumeTests(tests);
    } catch (error) {
      console.error('Error loading resume match tests:', error);
      toast({
        title: "Error",
        description: "Failed to load resume match tests. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectTest = (testId: string, checked: boolean) => {
    const newSelected = new Set(selectedTests);
    if (checked) {
      newSelected.add(testId);
    } else {
      newSelected.delete(testId);
    }
    setSelectedTests(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedTests(new Set(resumeTests.map(test => test.id)));
    } else {
      setSelectedTests(new Set());
    }
  };

  const handleBulkDelete = async () => {
    try {
      setIsBulkDeleting(true);
      const deletePromises = Array.from(selectedTests).map(testId => 
        ResumeMatchTestService.deleteResumeMatchTest(testId)
      );
      
      await Promise.all(deletePromises);
      
      toast({
        title: "Tests Deleted",
        description: `${selectedTests.size} resume match test(s) have been removed from your library.`
      });
      
      // Clear selections
      setSelectedTests(new Set());
      setShowBulkDeleteDialog(false);
      
      // Clear selected test if it was deleted
      if (selectedTest && selectedTests.has(selectedTest.id)) {
        setSelectedTest(null);
      }
      
      // Refresh the library
      await loadResumeTests();
      
    } catch (error) {
      console.error('Error deleting resume tests:', error);
      toast({
        title: "Bulk Delete Failed",
        description: "Failed to delete some resume tests. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleDeleteTest = async (testId: string, title: string) => {
    if (!confirm(`Are you sure you want to delete "${title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setIsDeleting(testId);
      await ResumeMatchTestService.deleteResumeMatchTest(testId);
      
      toast({
        title: "Test Deleted",
        description: "Resume match test has been removed from your library.",
      });
      
      if (selectedTest?.id === testId) {
        setSelectedTest(null);
      }
      
      // Remove from selected tests if it was selected
      const newSelected = new Set(selectedTests);
      newSelected.delete(testId);
      setSelectedTests(newSelected);
      
      await loadResumeTests();
      
    } catch (error) {
      console.error('Error deleting resume test:', error);
      toast({
        title: "Delete Failed",
        description: "Failed to delete resume test. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getMatchScoreColor = (score?: number) => {
    if (!score) return 'text-muted-foreground border-muted-foreground bg-muted/20';
    if (score >= 80) return 'text-green-700 border-green-600 bg-white';
    if (score >= 60) return 'text-blue-700 border-blue-600 bg-white';
    if (score >= 40) return 'text-orange-700 border-orange-600 bg-white';
    return 'text-red-700 border-red-600 bg-white';
  };

  const getMatchScoreLabel = (score?: number) => {
    if (!score) return 'No Score';
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Poor';
  };

  const getCardBg = () => {
    const gradients = [
      'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 border-blue-200/60',
      'bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 border-emerald-200/60',
      'bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 border-violet-200/60',
      'bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 border-orange-200/60',
      'bg-gradient-to-br from-rose-50 via-pink-50 to-red-50 border-rose-200/60'
    ];
    return gradients[Math.floor(Math.random() * gradients.length)];
  };

  const sortResumeTests = (tests: ResumeMatchTest[], sortBy: string): ResumeMatchTest[] => {
    const sorted = [...tests];
    
    switch (sortBy) {
      case 'date-desc':
        return sorted.sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime());
      case 'date-asc':
        return sorted.sort((a, b) => new Date(a.dateCreated).getTime() - new Date(b.dateCreated).getTime());
      case 'title-asc':
        return sorted.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      case 'title-desc':
        return sorted.sort((a, b) => (b.title || '').localeCompare(a.title || ''));
      case 'job-title-asc':
        return sorted.sort((a, b) => (a.jobTitle || '').localeCompare(b.jobTitle || ''));
      case 'job-title-desc':
        return sorted.sort((a, b) => (b.jobTitle || '').localeCompare(a.jobTitle || ''));
      case 'score-desc':
        return sorted.sort((a, b) => (b.matchScore || 0) - (a.matchScore || 0));
      case 'score-asc':
        return sorted.sort((a, b) => (a.matchScore || 0) - (b.matchScore || 0));
      case 'company-asc':
        return sorted.sort((a, b) => (a.companyName || '').localeCompare(b.companyName || ''));
      case 'company-desc':
        return sorted.sort((a, b) => (b.companyName || '').localeCompare(a.companyName || ''));
      default:
        return sorted;
    }
  };

  const sortedResumeTests = sortResumeTests(resumeTests, sortBy);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>Authentication Required</CardTitle>
            <CardDescription>Please log in to access your resume match tests.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your resume match tests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Custom Header matching tailored documents library page */}
      <div className="bg-gradient-to-r from-accent-light/30 to-primary-light/20 border-b border-border shadow-sm backdrop-blur-sm">
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={onBack}
                className="h-9 w-9 p-0"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="space-y-1">
                <h1 className="text-2xl font-bold text-foreground">Match Score Tested Resumes</h1>
                <p className="text-sm text-muted-foreground">View your resume analysis results and match scores</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-primary/10 to-accent/10 rounded-full border border-primary/20 shadow-sm">
              <span className="text-xs font-medium text-muted-foreground">powered by</span>
              <span className="text-sm font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">TailorWiz</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto py-6 space-y-6">
        {/* Centered gradient headline */}
        <div className="text-center mb-8">
          <h2 className="text-4xl font-bold mb-4">
            <span className="bg-gradient-to-r from-primary via-accent to-primary-hover bg-clip-text text-transparent">
              Your Resume Match Test Library
            </span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Comprehensive analysis results showing how well your resumes match specific job requirements and descriptions.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>Resume Match Tests ({resumeTests.length})</CardTitle>
              <CardDescription>
                Resumes analyzed against specific job descriptions
              </CardDescription>
            </CardHeader>
            <CardContent>
              {resumeTests.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No resume tests found</h3>
                  <p className="text-muted-foreground">
                    Run resume analysis to start building your test library.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Controls Header */}
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-4">
                      {/* Bulk Selection */}
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={selectedTests.size === resumeTests.length && resumeTests.length > 0}
                          onCheckedChange={(checked) => handleSelectAll(checked as boolean)}
                        />
                        <span className="text-sm font-medium">
                          {selectedTests.size === 0 
                            ? "Select All" 
                            : `${selectedTests.size} of ${resumeTests.length} selected`
                          }
                        </span>
                      </div>
                      
                      {/* Sort Controls */}
                      <div className="flex items-center gap-2">
                        <ArrowUpDown className="w-4 h-4 text-muted-foreground" />
                        <Select value={sortBy} onValueChange={setSortBy}>
                          <SelectTrigger className="w-52">
                            <SelectValue placeholder="Sort by..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="date-desc">Date (Newest First)</SelectItem>
                            <SelectItem value="date-asc">Date (Oldest First)</SelectItem>
                            <SelectItem value="title-asc">File Name (A-Z)</SelectItem>
                            <SelectItem value="title-desc">File Name (Z-A)</SelectItem>
                            <SelectItem value="job-title-asc">Job Title (A-Z)</SelectItem>
                            <SelectItem value="job-title-desc">Job Title (Z-A)</SelectItem>
                            <SelectItem value="score-desc">Match Score (High-Low)</SelectItem>
                            <SelectItem value="score-asc">Match Score (Low-High)</SelectItem>
                            <SelectItem value="company-asc">Company (A-Z)</SelectItem>
                            <SelectItem value="company-desc">Company (Z-A)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {selectedTests.size > 0 && (
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => setShowBulkDeleteDialog(true)}
                        className="flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete Selected ({selectedTests.size})
                      </Button>
                    )}
                  </div>

                  {sortedResumeTests.map((test) => (
                    <Card 
                      key={test.id}
                      className={`cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] transform ${getCardBg()}`}
                      onClick={() => {
                        setSelectedTest(test);
                        setShowBreakdown(true);
                      }}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center gap-6">
                          {/* Selection Checkbox */}
                          <div className="flex items-center">
                            <Checkbox
                              checked={selectedTests.has(test.id)}
                              onCheckedChange={(checked) => handleSelectTest(test.id, checked as boolean)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>

                          {/* Enhanced Match Score Circle with Star Badge */}
                          <div className="flex-shrink-0">
                            <div className="relative">
                              <div className={`w-20 h-20 rounded-full border-4 border-blue-600 flex flex-col items-center justify-center shadow-lg ${getMatchScoreColor(test.matchScore)}`}>
                                <div className="text-lg font-bold">{test.matchScore}%</div>
                                <div className="text-xs font-medium">{getMatchScoreLabel(test.matchScore)}</div>
                              </div>
                              
                              <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-blue-400 to-blue-500 rounded-full flex items-center justify-center shadow-sm border border-blue-600">
                                <BarChart3 className="w-3 h-3 text-white" />
                              </div>
                            </div>
                          </div>

                          {/* Enhanced Resume Details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-4">
                              <div className="min-w-0 flex-1">
                                <h3 className="font-bold text-xl leading-tight mb-2 text-foreground/90 truncate">
                                  {test.title}
                                </h3>
                                
                                <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mb-3">
                                  <div className="flex items-center gap-1 bg-white/70 px-2 py-1 rounded-md border border-muted">
                                    <Calendar className="w-4 h-4" />
                                    <span className="font-medium">{formatDate(test.dateCreated)}</span>
                                  </div>
                                  
                                  <div className="flex items-center gap-1 bg-blue-50 px-2 py-1 rounded-md border border-blue-200">
                                    <BarChart3 className="w-4 h-4 text-blue-600" />
                                    <span className="font-medium text-blue-700">Match Tested</span>
                                  </div>
                                </div>

                                {/* Enhanced Badge Section */}
                                <div className="flex flex-wrap gap-2 mb-3">
                                  {test.jobTitle && (
                                    <Badge variant="outline" className="flex items-center gap-1 bg-blue-50 border-blue-200 text-blue-700">
                                      <Target className="w-3 h-3" />
                                      {test.jobTitle}
                                    </Badge>
                                  )}
                                  
                                  {test.companyName && (
                                    <Badge variant="outline" className="flex items-center gap-1 bg-purple-50 border-purple-200 text-purple-700">
                                      <Building className="w-3 h-3" />
                                      {test.companyName}
                                    </Badge>
                                  )}

                                  {test.fileName && (
                                    <Badge variant="secondary" className="bg-slate-100 text-slate-700 border border-slate-200">
                                      {test.fileName}
                                    </Badge>
                                  )}
                                </div>

                                {/* Enhanced Match Score Display */}
                                <div className="bg-white/80 rounded-lg p-3 border border-muted/50">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <div className="text-sm">
                                        <span className="text-muted-foreground">Match Score: </span>
                                        <span className="font-bold text-2xl text-blue-600">{test.matchScore}%</span>
                                      </div>
                                    </div>
                                    
                                    <Badge variant="outline" className={`text-sm font-semibold ${getMatchScoreColor(test.matchScore)?.replace('bg-white', 'bg-opacity-20')}`}>
                                      {getMatchScoreLabel(test.matchScore)} Match
                                    </Badge>
                                  </div>
                                </div>
                              </div>

                              {/* Enhanced Action Buttons */}
                              <div className="flex flex-col gap-2 flex-shrink-0">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedTest(test);
                                    setShowBreakdown(true);
                                  }}
                                  title="View Full Report"
                                  className="flex items-center gap-2 bg-white/80 hover:bg-white border-2 border-blue-200 hover:border-blue-400 text-blue-700 hover:text-blue-800 shadow-sm"
                                >
                                  <Eye className="w-4 h-4" />
                                  View Report
                                </Button>

                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteTest(test.id, test.title);
                                  }}
                                  disabled={isDeleting === test.id}
                                  title="Delete Test"
                                  className="flex items-center gap-2 bg-white/80 hover:bg-white border-2 border-red-200 hover:border-red-400 text-red-700 hover:text-red-800 shadow-sm"
                                >
                                  {isDeleting === test.id ? (
                                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <Trash2 className="w-4 h-4" />
                                  )}
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Full Report Dialog */}
      {selectedTest && (
        <ResumeMatchBreakdown
          isOpen={showBreakdown}
          onClose={() => setShowBreakdown(false)}
          resumeText={selectedTest.content}
          resumeType="original"
          matchScore={selectedTest.matchScore}
          jobRequirements={selectedTest.analysisResults?.jobRequirements || []}
          jobResponsibilities={selectedTest.analysisResults?.jobResponsibilities || []}
          jobSkills={selectedTest.analysisResults?.jobSkills || []}
          jobTitle={selectedTest.jobTitle}
          companyName={selectedTest.companyName}
        />
      )}

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Selected Tests</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedTests.size} resume match test{selectedTests.size !== 1 ? 's' : ''}? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkDelete}
              disabled={isBulkDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isBulkDeleting ? "Deleting..." : "Delete Tests"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}